import { memo, CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import cn from 'bem-cn';

import { Popover, Icon } from '../UI';

import { NavigationProps, NavigationElement, NavigationElementType } from './Navigation.types';
import { useNavigationPosition } from './Navigation.hooks';
import { NAVIGATION_ALICE_LOGO, NAVIGATION_ALICE_URL, NAVIGATION_ICON_SIZES } from './Navigation.constants';
import './Navigation.scss';

const b = cn('navigation');

export const Navigation = memo<NavigationProps>(props => {
    const { elements, footerRef } = props;

    const positionState = useNavigationPosition({ footerRef: footerRef });

    const renderElement = (element: NavigationElement, index: number, type: NavigationElementType) => {
        const iconSize = NAVIGATION_ICON_SIZES[type];

        const content = (
            <>
                {element.icon && <Icon className={b('icon')} name={element.icon} size={iconSize} />}
                <span className={b('text')}>{element.text}</span>
            </>
        );

        const className = b('element', {
            active: element.active,
            accent: element.accent,
            [type]: true,
        }).mix(element.className);

        if (element.to) {
            return (
                <Link key={`${type}-${index}`} to={element.to} className={className}>
                    {content}
                </Link>
            );
        }

        const buttonContent = (
            <button
                key={`${type}-${index}`}
                type="button"
                className={className}
                onClick={e => {
                    e.stopPropagation();
                    element.onClick?.();
                }}
            >
                {content}
            </button>
        );

        if (element.usePopover) {
            return (
                <Popover
                    position="top"
                    key={`${type}-${index}`}
                    content={element.popoverContent}
                    className={`popover-${type}`}
                    contentClassName={element.popoverClassName}
                    autoClose={element.autoClosePopover}
                >
                    {buttonContent}
                </Popover>
            );
        }

        return buttonContent;
    };

    const totalLength = Math.max(elements.desktop.length, elements.mobile.length);

    if (totalLength === 0) {
        return null;
    }

    const navigationStyle: CSSProperties = footerRef
        ? { position: positionState.position, bottom: `${positionState.bottom}px` }
        : {};

    return (
        <nav
            style={navigationStyle}
            className={b({
                'single-button': totalLength === 1,
                'many-buttons': totalLength === 4,
            })}
        >
            {totalLength === 4 && (
                <a className={b('alice')} href={NAVIGATION_ALICE_URL}>
                    <img src={NAVIGATION_ALICE_LOGO} alt="Алиса" />
                </a>
            )}
            {elements.desktop.map((element, index) => renderElement(element, index, NavigationElementType.Desktop))}
            {elements.mobile.map((element, index) => renderElement(element, index, NavigationElementType.Mobile))}
        </nav>
    );
});
